import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import type { SosAlert } from '@/features/sos/types';

/**
 * An SOS alert in the alerts history feed (slice 85) — the FOURTEENTH item shape.
 *
 * ONE TONE: SOS is ALWAYS `critical` (a driver pressed the panic button). It wears
 * the loudest attention styling the feed has — the error-toned tag and the accent
 * unread dot — exactly like `TamperRow`, which it is modelled on.
 *
 * 🔴 IT CARRIES ITS ACKNOWLEDGEMENT (PLAN §8.4 / §9b). Unlike every other feed row,
 * an SOS records WHO closed it and WHEN. The row shows one of two states:
 *   - acknowledged → "Acknowledged by {name} at {time}" (the audit trail the
 *     customer sees), quiet and settled;
 *   - open → a loud "Not acknowledged" state in the SOS tone, so an unanswered
 *     panic alarm reads as unanswered in the history too.
 *
 * 🔴 LOCATION IS THE PIN FROM THE LAST VALID FIX, NOT "WHERE THE PRESS HAPPENED",
 * AND IT MAY NOT EXIST — position-27 discipline, mirroring TamperRow:
 *   - with a pin → a select button that centres the fleet map on it (and marks
 *     read); on vehicle detail (no map) selecting just marks read;
 *   - without a pin → a plain Link to the vehicle, never a map-select onto nothing.
 *
 * 🔴 PARALLEL-BUILD NOTE: `SosAlert` is the local mirror in `features/sos/types.ts`
 * until the api's `SosAlertZ` lands (see that file). The feed union in
 * `features/alerts/types.ts` does not yet include this leg, so `FeedRow` narrows to
 * it by a `type === 'sos'` check with a cast; when the api adds the 14th leg that
 * becomes an ordinary `isSos` narrowing with no cast. This row is unchanged either
 * way — it reads `SosAlert` fields only.
 */
export function SosRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: SosAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: () => void;
  t: TFunction;
}) {
  const hasPin = alert.observedLat != null && alert.observedLon != null;
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const acknowledged = alert.acknowledgedAt != null;
  const ackTime = alert.acknowledgedAt
    ? formatClock(locale, Date.parse(alert.acknowledgedAt), timeZone)
    : '';

  const inner = (
    <div className="flex items-start gap-2.5">
      <span
        aria-hidden="true"
        className={[
          'mt-1.5 size-2 flex-none rounded-full',
          alert.read ? 'bg-transparent' : 'bg-raad-accent',
        ].join(' ')}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={[
              'truncate text-sm',
              alert.read ? 'font-medium text-raad-muted' : 'font-semibold text-raad-text',
            ].join(' ')}
          >
            {t('sos.page.title')}
          </span>
          <span
            data-testid="sos-tag"
            className="flex-none rounded-md bg-raad-sos/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-sos"
          >
            {t('sos.tag')}
          </span>
          {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
        </div>

        {plate ? (
          <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
        ) : null}

        <div className="mt-1 text-[12px] text-raad-muted">
          {t('alerts.observedAt', { time: observedTime })}
        </div>

        {/* The acknowledgement line — the audit trail (PLAN §8.4). */}
        {acknowledged ? (
          <div data-testid="sos-ack" className="mt-1 text-[12px] font-medium text-raad-muted">
            {t('sos.acknowledgedBy', { name: alert.acknowledgedByName ?? t('sos.someone'), time: ackTime })}
          </div>
        ) : (
          <div
            data-testid="sos-unack"
            className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-semibold text-raad-sos"
          >
            <span className="size-1.5 rounded-full bg-raad-sos" aria-hidden="true" />
            {t('sos.notAcknowledged')}
          </div>
        )}
      </div>
    </div>
  );

  const containerClass = [
    'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
    selected
      ? 'border-raad-primary/40 bg-raad-primary/10'
      : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
  ].join(' ');

  return (
    <li>
      {hasPin ? (
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          data-testid="sos-row"
          data-type="sos"
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="true"
          className={containerClass}
        >
          {inner}
        </button>
      ) : (
        <Link
          to={`/vehicles/${alert.vehicleId}`}
          data-testid="sos-row"
          data-type="sos"
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="false"
          className={containerClass}
        >
          {inner}
        </Link>
      )}
    </li>
  );
}
